
import { Transaction, CreditCard } from '@/types';
import { localStringToDate, dateToLocalString } from '@/utils/dateFormatUtils';
import { getEffectiveAmount, calculateTransactionStatus } from '@/utils/fifoPaymentUtils';
import { processCardTransactionsForReports } from '@/utils/cardTransactionProcessor';

/**
 * FUNÇÃO UNIFICADA: Calcula a data efetiva de uma transação para cálculos financeiros
 * 
 * REGRAS:
 * - Receitas: sempre usam a data original
 * - Despesas sem cartão: sempre usam a data original
 * - Despesas de cartão (TODAS, independente de status): usam a data de vencimento da fatura
 *   Isso significa que uma compra no cartão só afeta o saldo quando a fatura vence
 * 
 * Esta função deve ser usada em TODOS os lugares que calculam saldos/fluxo de caixa
 */
export type CardExpenseDisplayMode = 'due_date' | 'transaction_date';

export const getTransactionEffectiveDate = (
  transaction: Transaction,
  cards: CreditCard[],
  cardExpenseDisplayMode: CardExpenseDisplayMode = 'due_date'
): string => {
  // Receitas: sempre data original
  if (transaction.type === 'receita') {
    return transaction.date;
  }
  
  // PAGAMENTOS DE FATURA: sempre data real (é transação de conta, não do cartão)
  if (transaction.isBillPayment) {
    return transaction.date;
  }
  
  // Despesas sem cartão: data original
  if (!transaction.cardId) {
    return transaction.date;
  }
  
  // NOVA LÓGICA: Verificar preferência do usuário
  if (cardExpenseDisplayMode === 'transaction_date') {
    return transaction.date;
  }
  
  // Comportamento padrão: data de vencimento da fatura
  const card = cards.find(c => c.id === transaction.cardId);
  if (card && card.dueDate) {
    return calculateBillDueDate(transaction.date, card.dueDate, card.closingDate);
  }
  
  return transaction.date;
};

/**
 * Calcula a data de vencimento da fatura para uma transação de cartão
 * Lógica: Se a transação for APÓS o dia de fechamento, vai para a fatura do mês seguinte (+2 meses)
 *         Se a transação for ATÉ o dia de fechamento, vai para a fatura do mês atual (+1 mês)
 */
export const calculateBillDueDate = (
  transactionDate: string, 
  cardDueDay: number,
  closingDate?: number
): string => {
  const txDate = localStringToDate(transactionDate);
  const txDay = txDate.getDate();
  const txMonth = txDate.getMonth();
  const txYear = txDate.getFullYear();
  
  // Usar closingDate se disponível, senão inferir como ~7 dias antes do vencimento
  // Se vencimento é dia 3, fechamento seria aproximadamente dia 25 do mês anterior
  const effectiveClosingDate = closingDate ?? (cardDueDay <= 7 ? cardDueDay + 23 : cardDueDay - 7);
  
  let dueMonth: number;
  let dueYear: number = txYear;
  
  if (txDay > effectiveClosingDate) {
    // Transação APÓS fechamento → próximo ciclo (vence daqui a ~2 meses)
    dueMonth = txMonth + 2;
  } else {
    // Transação ATÉ o fechamento → ciclo atual (vence daqui a ~1 mês)
    dueMonth = txMonth + 1;
  }
  
  // Ajustar ano se necessário
  if (dueMonth > 11) {
    dueYear += Math.floor(dueMonth / 12);
    dueMonth = dueMonth % 12;
  }
  
  const dueDate = new Date(dueYear, dueMonth, cardDueDay);
  return dateToLocalString(dueDate);
};

/**
 * Agrupa transações de cartão por data de vencimento da fatura
 * NOVA LÓGICA: usa data real para transações pagas e data de vencimento para pendentes
 */
export const groupCardTransactionsByBillDate = (
  transactions: Transaction[],
  cards: CreditCard[]
): Transaction[] => {
  console.log('=== PROCESSING CARD TRANSACTIONS FOR REPORTS ===');
  
  // Usar novo processador que considera status de pagamento
  const processedTransactions = processCardTransactionsForReports(transactions, cards);
  
  console.log(`Processed ${processedTransactions.length} transactions`);
  
  return processedTransactions;
};

/**
 * Calcula faturas futuras com parcelamentos
 */
export const calculateFutureBillsWithInstallments = (
  transactions: Transaction[],
  cards: CreditCard[],
  startDate?: string,
  endDate?: string
): Array<{
  id: string;
  description: string;
  amount: number;
  type: 'despesa';
  category: string;
  categoryId: string;
  date: string;
  cardId: string;
  isGroupedBill: boolean;
  originalTransactions: Transaction[];
  isPendingBill?: boolean;
}> => {
  console.log('=== CALCULATING FUTURE BILLS WITH INSTALLMENTS ===');
  
  const futureBills: Array<any> = [];

  // Agrupar transações parceladas por cartão (excluir pagamentos de fatura)
  const installmentTransactions = transactions.filter(t => 
    t.cardId && t.type === 'despesa' && t.installments && t.installments > 1 && !t.isBillPayment
  );

  console.log('Installment transactions found:', installmentTransactions.length);

  // Agrupar por originalTransactionId ou por identificador único
  const installmentGroups: { [key: string]: Transaction[] } = {};
  
  installmentTransactions.forEach(transaction => {
    const groupKey = transaction.parentTransactionId || 
      `${transaction.description?.replace(/ - Parcela \d+\/\d+/, '')}_${transaction.cardId}_${transaction.installments}`;
    
    if (!installmentGroups[groupKey]) {
      installmentGroups[groupKey] = [];
    }
    installmentGroups[groupKey].push(transaction);
  });

  console.log('Installment groups:', Object.keys(installmentGroups).length);

  // Para cada grupo de parcelamento, calcular faturas futuras
  Object.entries(installmentGroups).forEach(([groupKey, groupTransactions]) => {
    const firstTransaction = groupTransactions[0];
    const card = cards.find(c => c.id === firstTransaction.cardId);
    if (!card || !card.dueDate) return;

    const installmentAmount = Math.abs(firstTransaction.amount);
    const totalInstallments = firstTransaction.installments || 1;
    const firstTransactionDate = new Date(firstTransaction.date);
    
    // Calcular todas as datas de vencimento das faturas
    for (let i = 1; i <= totalInstallments; i++) {
      const installmentDate = new Date(firstTransactionDate);
      installmentDate.setMonth(installmentDate.getMonth() + (i - 1));
      
      const billDueDate = calculateBillDueDate(dateToLocalString(installmentDate), card.dueDate, card.closingDate);
      const billDate = localStringToDate(billDueDate);
      
      // Verificar se a fatura está no período solicitado (se especificado)
      if (startDate && endDate) {
        const start = localStringToDate(startDate);
        const end = localStringToDate(endDate);
        if (billDate < start || billDate > end) continue;
      }
      
      const billId = `future_bill_${firstTransaction.cardId}_${billDueDate}_${groupKey}_${i}`;
      
      // Verificar se já existe uma transação para esta parcela específica
      const existingTransaction = groupTransactions.find(t => 
        t.installmentNumber === i || 
        t.description?.includes(`Parcela ${i}/`)
      );
      
      futureBills.push({
        id: billId,
        description: `Fatura ${card.name} - ${firstTransaction.description?.replace(/ - Parcela \d+\/\d+/, '')} (${i}/${totalInstallments})`,
        amount: installmentAmount,
        type: 'despesa' as const,
        category: 'Fatura de Cartão',
        categoryId: firstTransaction.categoryId,
        date: billDueDate,
        cardId: firstTransaction.cardId,
        isGroupedBill: true,
        originalTransactions: existingTransaction ? [existingTransaction] : [],
        isPendingBill: !existingTransaction || existingTransaction.status !== 'paid',
        installmentInfo: {
          current: i,
          total: totalInstallments,
          groupKey: groupKey,
          existingTransactionId: existingTransaction?.id
        }
      });
    }
  });

  console.log('Future bills calculated:', futureBills.length);
  return futureBills;
};

/**
 * Gera faturas automaticamente para um cartão com parcelamentos
 */
export const generateFutureBillsForCard = (
  cardId: string,
  transactions: Transaction[],
  cards: CreditCard[]
): Array<{
  id: string;
  cardId: string;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  totalAmount: number;
  paidAmount: number;
  status: 'pendente' | 'paga' | 'parcialmente_paga';
  transactionIds: string[];
  installmentInfo?: {
    description: string;
    current: number;
    total: number;
    amount: number;
  }[];
}> => {
  console.log('=== GENERATING FUTURE BILLS FOR CARD ===', cardId);
  
  const card = cards.find(c => c.id === cardId);
  if (!card || !card.dueDate) return [];

  const futureBills: Array<any> = [];
  const billsMap: { [dueDate: string]: any } = {};

  // CORRIGIDO: Buscar TODAS as transações de despesa deste cartão (não só parceladas)
  const allCardExpenses = transactions.filter(t => 
    t.cardId === cardId && 
    t.type === 'despesa' &&
    !t.isBillPayment
  );

  console.log('All card expenses for card:', allCardExpenses.length);

  // Separar transações parceladas das avulsas
  const installmentTransactions = allCardExpenses.filter(t => t.installments && t.installments > 1);
  const singleTransactions = allCardExpenses.filter(t => !t.installments || t.installments <= 1);

  console.log('Installment transactions:', installmentTransactions.length);
  console.log('Single transactions:', singleTransactions.length);

  // Agrupar por originalTransactionId
  const installmentGroups: { [key: string]: Transaction[] } = {};
  
  installmentTransactions.forEach(transaction => {
    const groupKey = transaction.parentTransactionId || 
      `${transaction.description?.replace(/ - Parcela \d+\/\d+/, '')}_${transaction.cardId}_${transaction.installments}`;
    
    if (!installmentGroups[groupKey]) {
      installmentGroups[groupKey] = [];
    }
    installmentGroups[groupKey].push(transaction);
  });

  // Para cada grupo, gerar faturas mensais
  Object.entries(installmentGroups).forEach(([groupKey, groupTransactions]) => {
    const firstTransaction = groupTransactions[0];
    const installmentAmount = Math.abs(firstTransaction.amount);
    const totalInstallments = firstTransaction.installments || 1;
    const firstTransactionDate = new Date(firstTransaction.date);
    
    for (let i = 1; i <= totalInstallments; i++) {
      const installmentDate = new Date(firstTransactionDate);
      installmentDate.setMonth(installmentDate.getMonth() + (i - 1));
      
      const billDueDate = calculateBillDueDate(dateToLocalString(installmentDate), card.dueDate, card.closingDate);
      
      // Verificar se já existe transação para esta parcela
      const existingTransaction = groupTransactions.find(t => 
        t.installmentNumber === i || 
        t.description?.includes(`Parcela ${i}/`)
      );
      
      if (!billsMap[billDueDate]) {
        // Calcular período da fatura
        const dueDate = localStringToDate(billDueDate);
        const periodEnd = new Date(dueDate);
        periodEnd.setDate(periodEnd.getDate() - 1);
        
        const periodStart = new Date(periodEnd);
        periodStart.setMonth(periodStart.getMonth() - 1);
        periodStart.setDate(periodStart.getDate() + 1);
        
        billsMap[billDueDate] = {
          id: `future_bill_${cardId}_${billDueDate}`,
          cardId,
          periodStart: dateToLocalString(periodStart),
          periodEnd: dateToLocalString(periodEnd),
          dueDate: billDueDate,
          totalAmount: 0,
          paidAmount: 0,
          status: 'pendente' as const,
          transactionIds: [],
          installmentInfo: []
        };
      }
      
      // Adicionar informação da parcela à fatura
      billsMap[billDueDate].installmentInfo.push({
        description: firstTransaction.description?.replace(/ - Parcela \d+\/\d+/, '') || '',
        current: i,
        total: totalInstallments,
        amount: installmentAmount
      });
      
      billsMap[billDueDate].totalAmount += installmentAmount;
      
      if (existingTransaction) {
        billsMap[billDueDate].transactionIds.push(existingTransaction.id);
        if (existingTransaction.status === 'paid') {
          billsMap[billDueDate].paidAmount += installmentAmount;
        }
      }
    }
  });

  // CORRIGIDO: Adicionar transações avulsas (não parceladas) às suas respectivas faturas
  singleTransactions.forEach(transaction => {
    const billDueDate = calculateBillDueDate(transaction.date, card.dueDate, card.closingDate);
    
    if (!billsMap[billDueDate]) {
      const dueDate = localStringToDate(billDueDate);
      const periodEnd = new Date(dueDate);
      periodEnd.setDate(periodEnd.getDate() - 1);
      
      const periodStart = new Date(periodEnd);
      periodStart.setMonth(periodStart.getMonth() - 1);
      periodStart.setDate(periodStart.getDate() + 1);
      
      billsMap[billDueDate] = {
        id: `future_bill_${cardId}_${billDueDate}`,
        cardId,
        periodStart: dateToLocalString(periodStart),
        periodEnd: dateToLocalString(periodEnd),
        dueDate: billDueDate,
        totalAmount: 0,
        paidAmount: 0,
        status: 'pendente' as const,
        transactionIds: [],
        installmentInfo: []
      };
    }
    
    const txAmount = transaction.originalAmount || Math.abs(transaction.amount);
    billsMap[billDueDate].totalAmount += txAmount;
    billsMap[billDueDate].transactionIds.push(transaction.id);
    
    // Considerar paidPortion para transações avulsas
    const paidPortion = transaction.paidPortion || 0;
    if (paidPortion > 0) {
      billsMap[billDueDate].paidAmount += paidPortion;
    }
  });

  // Determinar status das faturas
  Object.values(billsMap).forEach((bill: any) => {
    if (bill.paidAmount >= bill.totalAmount) {
      bill.status = 'paga';
    } else if (bill.paidAmount > 0) {
      bill.status = 'parcialmente_paga';
    } else {
      bill.status = 'pendente';
    }
    
    futureBills.push(bill);
  });

  console.log('Generated future bills:', futureBills.length);
  return futureBills;
};

/**
 * Agrupa transações de cartão por fatura, somando os valores EFETIVOS (considerando pagamentos)
 */
export const groupTransactionsByBill = (
  transactions: Transaction[],
  cards: CreditCard[]
): Array<{
  id: string;
  description: string;
  amount: number;
  type: 'despesa';
  category: string;
  categoryId: string;
  date: string; // Data de vencimento da fatura
  cardId: string;
  isGroupedBill: boolean;
  originalTransactions: Transaction[];
}> => {
  console.log('=== GROUPING TRANSACTIONS BY BILL ===');
  
  // CORRIGIDO: Excluir pagamentos de fatura (têm cardId mas não são compras do cartão)
  const cardTransactions = transactions.filter(t => 
    t.cardId && t.type === 'despesa' && !t.isBillPayment
  );

  console.log('Card transactions to group:', cardTransactions.length);

  const billGroups: { [key: string]: Transaction[] } = {};

  cardTransactions.forEach(transaction => {
    const card = cards.find(c => c.id === transaction.cardId);
    if (!card || !card.dueDate) return;

    const billDueDate = calculateBillDueDate(transaction.date, card.dueDate, card.closingDate);
    const key = `${transaction.cardId}_${billDueDate}`;

    if (!billGroups[key]) {
      billGroups[key] = [];
    }
    billGroups[key].push(transaction);
  });

  const groupedBills = Object.entries(billGroups).map(([key, txns]) => {
    const [cardId, dueDate] = key.split('_');
    const card = cards.find(c => c.id === cardId);
    
    // Calculate EFFECTIVE amount considering payments (FIFO)
    let totalEffectiveAmount = 0;
    let hasAnyPending = false;
    
    txns.forEach(t => {
      const status = calculateTransactionStatus(t);
      if (status !== 'paid') {
        hasAnyPending = true;
        const effectiveAmount = Math.abs(getEffectiveAmount(t));
        totalEffectiveAmount += effectiveAmount;
        
        console.log(`Transaction ${t.id}: status=${status}, effective=${effectiveAmount}`);
      }
    });
    
    console.log(`Bill ${key}: totalEffective=${totalEffectiveAmount}, hasAnyPending=${hasAnyPending}`);
    
    // Only show bills that have pending amounts
    if (!hasAnyPending || totalEffectiveAmount <= 0) {
      return null;
    }
    
    return {
      id: `bill_${key}`,
      description: `Fatura ${card?.name || 'Cartão'} - ${new Date(dueDate).toLocaleDateString('pt-BR')}`,
      amount: totalEffectiveAmount,
      type: 'despesa' as const,
      category: 'Fatura de Cartão',
      categoryId: 'bill_category',
      date: dueDate,
      cardId,
      isGroupedBill: true,
      originalTransactions: txns
    };
  }).filter(bill => bill !== null);

  console.log('Final grouped bills:', groupedBills.length);
  return groupedBills as Array<{
    id: string;
    description: string;
    amount: number;
    type: 'despesa';
    category: string;
    categoryId: string;
    date: string;
    cardId: string;
    isGroupedBill: boolean;
    originalTransactions: Transaction[];
  }>;
};
